// assets/video-stories.js
// <video-stories>: hero de video tipo "stories" con dots de progreso real (timeupdate),
// transición automática al siguiente video, pausa/play, navegación por click/swipe.
// Es el hero (LCP) cuando data-is-hero="true": init inmediato, sin esperar IntersectionObserver.

(function () {
  'use strict';

  if (customElements.get('video-stories')) return;

  class VideoStories extends HTMLElement {
    connectedCallback() {
      // Si es el hero, inicializa de inmediato (siempre visible al cargar).
      // Si no, espera a que entre en viewport para no reproducir videos fuera de pantalla.
      if (this.dataset.isHero === 'true') {
        this.init();
        return;
      }

      this._io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.init();
        },
        { rootMargin: '200px' }
      );
      this._io.observe(this);

      this._fallback = setTimeout(() => {
        if (!this._initialized) this.init();
      }, 2000);
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;
      clearTimeout(this._fallback);

      this.slides = Array.from(this.querySelectorAll('[data-vs-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-vs-dot]'));
      this.toggleBtn = this.querySelector('[data-vs-toggle-play]');
      this.iconPause = this.querySelector('[data-vs-icon-pause]');
      this.iconPlay = this.querySelector('[data-vs-icon-play]');

      this.currentIndex = 0;
      this.isPaused = false;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.fallbackTimer = null;

      if (!this.slides.length) return;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.index, 10);
          this.goToSlide(idx, true);
        });
      });

      this.toggleBtn?.addEventListener('click', () => this.togglePause());

      // Swipe básico en mobile (sin librerías): siguiente/anterior según dirección.
      this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });

      // Pausa cuando la pestaña no está visible (ahorra batería/datos, evita seguir contando progreso fuera de foco)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseCurrentVideo();
        } else if (!this.isPaused) {
          this.playCurrentVideo();
        }
      });

      this.activateSlide(0, { restart: true });
    }

    getVideoEl(slide) {
      return slide.querySelector('.video-stories__video');
    }

    activateSlide(index, { restart = false } = {}) {
      const prevSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];
      if (!nextSlide) return;

      // Detener y limpiar listeners del slide anterior
      if (prevSlide && prevSlide !== nextSlide) {
        prevSlide.classList.remove('is-active');
        const prevVideo = this.getVideoEl(prevSlide);
        if (prevVideo) {
          prevVideo.pause();
          prevVideo.removeEventListener('timeupdate', this._onTimeUpdate);
          prevVideo.removeEventListener('ended', this._onEnded);
        }
        this.clearDotAnimation(this.currentIndex);
      }

      this.currentIndex = index;
      nextSlide.classList.add('is-active');

      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });

      const video = this.getVideoEl(nextSlide);
      const duration = parseFloat(nextSlide.dataset.duration) || 6;

      if (video) {
        this.runVideoSlide(nextSlide, video, restart);
      } else {
        // Slide sin video (solo imagen): usa temporizador fijo basado en "duration" del bloque.
        this.runImageSlide(nextSlide, duration);
      }
    }

    runVideoSlide(slide, video, restart) {
      // Carga el video si aún no tiene src activo (slides no-hero usan preload="none").
      if (video.preload === 'none') {
        video.preload = 'auto';
        video.load();
      }

      if (restart) {
        try {
          video.currentTime = 0;
        } catch (e) {
          /* algunos navegadores lanzan si el video no está listo; se ignora */
        }
      }

      const slideDuration = parseFloat(slide.dataset.duration) || 6;

      this._onTimeUpdate = () => {
        if (!video.duration || isNaN(video.duration)) return;
        // El avance ocurre al límite que se cumpla primero: duración configurada del bloque
        // o duración real del video (lo que sea más corto).
        const effectiveDuration = Math.min(slideDuration, video.duration);
        const ratio = Math.min(video.currentTime / effectiveDuration, 1);
        this.setDotProgress(this.currentIndex, ratio * 100);
        if (video.currentTime >= effectiveDuration) {
          this.next();
        }
      };

      this._onEnded = () => this.next();

      video.addEventListener('timeupdate', this._onTimeUpdate);
      video.addEventListener('ended', this._onEnded);

      if (!this.isPaused) {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      }

      // Si por reduced-motion el navegador no autoplay, o el video falla, usa fallback por tiempo fijo del bloque.
      this.clearFallbackTimer();
      if (this.prefersReducedMotion) {
        video.pause();
        this.isPaused = true;
        this.updatePlayIcon();
        // En reduced-motion no auto-avanzamos: el usuario controla manualmente.
        this.setDotProgress(this.currentIndex, 0);
      } else {
        // Respaldo: si timeupdate/ended nunca disparan (video corrupto, error de red), fuerza avance.
        this.fallbackTimer = setTimeout(() => {
          if (this.currentIndex === this.slides.indexOf(slide)) this.next();
        }, (slideDuration + 1.5) * 1000);
      }
    }

    runImageSlide(slide, durationSec) {
      if (this.prefersReducedMotion) {
        this.setDotProgress(this.currentIndex, 0);
        return;
      }
      this.animateDotByTime(this.currentIndex, durationSec);
      this.clearFallbackTimer();
      this.fallbackTimer = setTimeout(() => {
        if (this.currentIndex === this.slides.indexOf(slide)) this.next();
      }, durationSec * 1000);
    }

    animateDotByTime(index, durationSec) {
      const dot = this.dots[index];
      const fill = dot?.querySelector('.video-stories__dot-fill');
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.width = '0%';
      fill.setAttribute('data-animating', '');
      requestAnimationFrame(() => {
        fill.style.transition = `width ${durationSec}s linear`;
        fill.style.width = '100%';
      });
    }

    setDotProgress(index, percent) {
      const dot = this.dots[index];
      const fill = dot?.querySelector('.video-stories__dot-fill');
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.width = `${percent}%`;
    }

    clearDotAnimation(index) {
      const dot = this.dots[index];
      const fill = dot?.querySelector('.video-stories__dot-fill');
      if (!fill) return;
      fill.removeAttribute('data-animating');
      fill.style.transition = 'none';
      fill.style.width = '0%';
    }

    clearFallbackTimer() {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
    }

    next() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.activateSlide(nextIndex, { restart: true });
    }

    prev() {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.activateSlide(prevIndex, { restart: true });
    }

    goToSlide(index, restart) {
      if (index === this.currentIndex) return;
      this.activateSlide(index, { restart: !!restart });
    }

    pauseCurrentVideo() {
      const slide = this.slides[this.currentIndex];
      const video = slide && this.getVideoEl(slide);
      video?.pause();
    }

    playCurrentVideo() {
      const slide = this.slides[this.currentIndex];
      const video = slide && this.getVideoEl(slide);
      if (video) {
        const p = video.play();
        if (p?.catch) p.catch(() => {});
      }
    }

    togglePause() {
      this.isPaused = !this.isPaused;
      const slide = this.slides[this.currentIndex];
      const video = slide && this.getVideoEl(slide);

      if (this.isPaused) {
        video?.pause();
        this.clearFallbackTimer();
        // Pausa también la animación visual del dot fijando su ancho actual.
        const dot = this.dots[this.currentIndex];
        const fill = dot?.querySelector('.video-stories__dot-fill');
        if (fill) {
          const computed = getComputedStyle(fill).width;
          fill.style.transition = 'none';
          fill.style.width = computed;
        }
      } else {
        if (video) {
          const p = video.play();
          if (p?.catch) p.catch(() => {});
        } else {
          // Slide de imagen: retoma animación por tiempo restante (simplificado: reinicia duración completa).
          const slideEl = this.slides[this.currentIndex];
          const durationSec = parseFloat(slideEl.dataset.duration) || 6;
          this.runImageSlide(slideEl, durationSec);
        }
      }
      this.updatePlayIcon();
    }

    updatePlayIcon() {
      if (this.iconPause) this.iconPause.hidden = this.isPaused;
      if (this.iconPlay) this.iconPlay.hidden = !this.isPaused;
      this.toggleBtn?.setAttribute('aria-label', this.isPaused ? 'Reproducir video' : 'Pausar video');
    }

    onTouchStart(e) {
      this._touchStartX = e.changedTouches[0].clientX;
    }

    onTouchEnd(e) {
      if (this._touchStartX == null) return;
      const deltaX = e.changedTouches[0].clientX - this._touchStartX;
      const threshold = 40;
      if (Math.abs(deltaX) < threshold) return;
      if (deltaX < 0) {
        this.next();
      } else {
        this.prev();
      }
      this._touchStartX = null;
    }

    disconnectedCallback() {
      this._io?.disconnect();
      this.clearFallbackTimer();
    }
  }

  customElements.define('video-stories', VideoStories);
})();